import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { setNavigate } from './lib/navigation';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import About from './pages/About';
import Blog from './pages/Blog';
import Careers from './pages/Careers';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AdminDashboard from './pages/AdminDashboard';
import StartupDashboard from './pages/StartupDashboard';
import StartupCompleteProfile from './pages/StartupCompleteProfile';
import InvestorDashboard from './pages/InvestorDashboard';
import InvestorNetworkDashboard from './pages/InvestorNetworkDashboard';
import InvestorNetworkInvestorsPage from './pages/InvestorNetworkInvestorsPage';
import InvestorNetworkInvestorDetail from './pages/InvestorNetworkInvestorDetail';
import EventOrganizerDashboard from './pages/EventOrganizerDashboard';
import ViewerDashboard from './pages/ViewerDashboard';
import InvestorNetworkDetail from './pages/InvestorNetworkDetail';
import InvestorNetworksList from './pages/InvestorNetworksList';
import InvestorDetail from './pages/InvestorDetail';
import StartupDetail from './pages/StartupDetail';
import PortfolioStartupDetail from './pages/PortfolioStartupDetail';
import PitchEventDetail from './pages/PitchEventDetail';
import PitchEventList from './pages/PitchEventList';
import SettingsPage from './pages/SettingsPage';
import InvestmentManagement from './pages/InvestmentManagement';
import InvestorsManagement from './pages/InvestorsManagement';
import ReportsPage from './pages/ReportsPage';
import MySubscription from './pages/MySubscription';
import BillingDashboard from './pages/BillingDashboard';
import PurchaseAddon from './pages/PurchaseAddon';
import InvestorActivityFee from './pages/InvestorActivityFee';
import AuditLogPage from './pages/AuditLogPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/AdminLayout';
import InvestorLayout from './components/InvestorLayout';

const INVESTOR_NETWORK_LEGACY_TABS = ['portfolio', 'startups', 'users', 'documents', 'activity', 'investors'];

/** Old /investor-network/dashboard/:tab? and /user/dashboard/:tab → /investor-network/... */
const InvestorNetworkLegacyPathRedirect = () => {
	const { tenantId, tab } = useParams();
	if (tab === 'investors') {
		return <Navigate to={`/${tenantId}/investor-network/investors`} replace />;
	}
	if (!tab || tab === 'portfolio' || !INVESTOR_NETWORK_LEGACY_TABS.includes(tab)) {
		return <Navigate to={`/${tenantId}/investor-network`} replace />;
	}
	return <Navigate to={`/${tenantId}/investor-network/${tab}`} replace />;
};

/** Redirects /:tenantId/user/startups/:id to investor-network startup detail. */
const RedirectToInvestorNetworkStartup = () => {
	const { tenantId, id } = useParams();
	return <Navigate to={`/${tenantId}/investor-network/startups/${id}`} replace />;
};

const AppRoutes = () => {
	const { user, isAuthenticated } = useAuth();
	const tenantPath = user?.tenantIdentifier || 'default';
	const navigate = useNavigate();

	// Set up navigation utility for use in interceptors
	useEffect(() => {
		setNavigate(navigate);

		// Listen for custom navigation events
		const handleNavigation = (event) => {
			navigate(event.detail.path);
		};
		window.addEventListener('spa-navigate', handleNavigation);

		return () => {
			window.removeEventListener('spa-navigate', handleNavigation);
		};
	}, [navigate]);

	return (
		<Routes>
			{/* Public routes - not under tenant */}
			<Route path="/login" element={<Login />} />
			<Route path="/signup" element={<Signup />} />
			<Route path="/verify-email" element={<VerifyEmail />} />
			<Route path="/forgot-password" element={<ForgotPassword />} />
			<Route path="/reset-password" element={<ResetPassword />} />
			<Route path="/about" element={<About />} />
			<Route path="/blog" element={<Blog />} />
			<Route path="/careers" element={<Careers />} />
			<Route path="/contact" element={<Contact />} />
			<Route path="/privacy" element={<PrivacyPolicy />} />
			<Route path="/terms" element={<TermsOfService />} />

			{/* Tenant Routes Wrapper */}
			<Route path="/:tenantId">
				{/* Show login page at tenant root */}
				<Route index element={<Login />} />
				{/* Admin Layout Routes */}
				<Route element={<AdminLayout />}>
					<Route
						path="dashboard"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="files"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="startups"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="startups/:id"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<StartupDetail />
							</ProtectedRoute>
						}
					/>
					<Route
						path="investors"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="investors/:id"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<InvestorDetail />
							</ProtectedRoute>
						}
					/>
					<Route
						path="investors/networks/:id"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<InvestorNetworkDetail />
							</ProtectedRoute>
						}
					/>
					<Route
						path="users"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="reports"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="events"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN', 'EVENT_ORGANIZER']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="events/:id"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN', 'EVENT_ORGANIZER']}>
								<PitchEventDetail />
							</ProtectedRoute>
						}
					/>
					<Route
						path="account"
						element={
							<ProtectedRoute allowedRoles={["ADMIN", "TENANT_ADMIN"]}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="settings"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="investments"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<InvestmentManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="audit-logs"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AuditLogPage />
							</ProtectedRoute>
						}
					/>
				</Route>
				<Route
					path="startup/dashboard"
					element={
						<ProtectedRoute allowedRoles={['STARTUP']}>
							<StartupDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="startup/complete-profile"
					element={
						<ProtectedRoute allowedRoles={['STARTUP']}>
							<StartupCompleteProfile />
						</ProtectedRoute>
					}
				/>
				<Route path="investor/dashboard/documents" element={<Navigate to="../documents" replace />} />
				<Route path="investor/dashboard/events" element={<Navigate to="../events" replace />} />
				<Route
					element={
						<ProtectedRoute allowedRoles={['INVESTOR']}>
							<InvestorLayout />
						</ProtectedRoute>
					}
				>
					<Route path="investor/dashboard" element={<InvestorDashboard />} />
					<Route path="investor/documents" element={<InvestorDashboard />} />
					<Route path="investor/events" element={<InvestorDashboard />} />
					<Route path="investor/startups/:id" element={<PortfolioStartupDetail />} />
					<Route path="investor/networks" element={<InvestorNetworksList />} />
					<Route path="investor/networks/:id" element={<InvestorNetworkDetail />} />
				</Route>
				<Route
					path="investor-network/investors"
					element={
						<ProtectedRoute allowedRoles={['INVESTOR_NETWORK', 'ADMIN', 'TENANT_ADMIN', 'MANAGEMENT']}>
							<InvestorNetworkInvestorsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="investor-network/investors/:id"
					element={
						<ProtectedRoute allowedRoles={['INVESTOR_NETWORK', 'ADMIN', 'TENANT_ADMIN', 'MANAGEMENT']}>
							<InvestorNetworkInvestorDetail />
						</ProtectedRoute>
					}
				/>
				<Route path="investor-network/networks" element={<Navigate to="../investor/networks" replace />} />
				<Route
					path="investor-network/networks/:id"
					element={
						<ProtectedRoute allowedRoles={['INVESTOR_NETWORK', 'INVESTOR', 'MANAGEMENT']}>
							<InvestorNetworkDetail />
						</ProtectedRoute>
					}
				/>
				<Route
					path="investor-network/startups/:id"
					element={
						<ProtectedRoute allowedRoles={['INVESTOR_NETWORK', 'MANAGEMENT']}>
							<PortfolioStartupDetail />
						</ProtectedRoute>
					}
				/>
				<Route path="investor-network/dashboard" element={<InvestorNetworkLegacyPathRedirect />} />
				<Route path="investor-network/dashboard/:tab" element={<InvestorNetworkLegacyPathRedirect />} />
				<Route
					path="investor-network"
					element={
						<ProtectedRoute allowedRoles={['INVESTOR_NETWORK', 'ADMIN', 'TENANT_ADMIN', 'MANAGEMENT']}>
							<InvestorNetworkDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="investor-network/:tab"
					element={
						<ProtectedRoute allowedRoles={['INVESTOR_NETWORK', 'ADMIN', 'TENANT_ADMIN', 'MANAGEMENT']}>
							<InvestorNetworkDashboard />
						</ProtectedRoute>
					}
				/>

				{/* Subscription plans are shown on my-subscription page; redirect old link */}
				<Route
					path="subscription/plans"
					element={<Navigate to="../my-subscription" replace />}
				/>
				<Route
					path="my-subscription"
					element={
						<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN', 'STARTUP', 'ENTREPRENEUR', 'INVESTOR', 'INVESTOR_NETWORK', 'MANAGEMENT']}>
							<MySubscription />
						</ProtectedRoute>
					}
				/>
				<Route
					path="billing"
					element={
						<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN', 'STARTUP', 'ENTREPRENEUR', 'INVESTOR', 'INVESTOR_NETWORK', 'MANAGEMENT']}>
							<BillingDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="purchase-addon"
					element={
						<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN', 'STARTUP', 'ENTREPRENEUR', 'INVESTOR', 'INVESTOR_NETWORK', 'MANAGEMENT']}>
							<PurchaseAddon />
						</ProtectedRoute>
					}
				/>
				<Route
					path="investor-activity-fee"
					element={
						<ProtectedRoute allowedRoles={['INVESTOR']}>
							<InvestorActivityFee />
						</ProtectedRoute>
					}
				/>

				<Route
					path="organizer/dashboard"
					element={
						<ProtectedRoute allowedRoles={['EVENT_ORGANIZER']}>
							<EventOrganizerDashboard />
						</ProtectedRoute>
					}
				/>
					{/* User (MANAGEMENT) uses same dashboard as investor network — redirect old user URLs */}
				<Route
					path="user/dashboard"
					element={
						<ProtectedRoute allowedRoles={['MANAGEMENT', 'ADMIN', 'TENANT_ADMIN']}>
							<Navigate to="../investor-network" replace />
						</ProtectedRoute>
					}
				/>
				<Route
					path="user/dashboard/:tab"
					element={
						<ProtectedRoute allowedRoles={['MANAGEMENT', 'ADMIN', 'TENANT_ADMIN']}>
							<InvestorNetworkLegacyPathRedirect />
						</ProtectedRoute>
					}
				/>
				<Route
					path="user/startups/:id"
					element={
						<ProtectedRoute allowedRoles={['MANAGEMENT']}>
							<RedirectToInvestorNetworkStartup />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/dashboard"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/files"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/startups"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/investors"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/events"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/users"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/reports"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
			</Route>

			<Route
				path="/"
				element={
					isAuthenticated ? (
						user?.role === 'ADMIN' || user?.role === 'TENANT_ADMIN' ? (
							<Navigate to={`/${tenantPath}/dashboard`} replace />
						) : user?.role === 'STARTUP' ? (
							<Navigate to={`/${tenantPath}/startup/dashboard`} replace />
						) : user?.role === 'INVESTOR' ? (
							<Navigate to={`/${tenantPath}/investor/dashboard`} replace />
						) : user?.role === 'INVESTOR_NETWORK' ? (
							<Navigate to={`/${tenantPath}/investor-network`} replace />
						) : user?.role === 'EVENT_ORGANIZER' ? (
							<Navigate to={`/${tenantPath}/organizer/dashboard`} replace />
						) : user?.role === 'MANAGEMENT' ? (
							<Navigate to={`/${tenantPath}/investor-network`} replace />
						) : user?.role === 'VIEWER' ? (
							<Navigate to={`/${tenantPath}/viewer/dashboard`} replace />
						) : (
							<Navigate to="/login" replace />
						)
					) : (
						<LandingPage />
					)
				}
			/>
		</Routes>
	);
};

const App = () => {
	return (
		<AuthProvider>
			<div className="min-h-screen bg-gray-50">
				<AppRoutes />
			</div>
		</AuthProvider>
	);
};

export default App;


